import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import SwipeIcon from "@mui/icons-material/Swipe";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Stream, StreamLayerDatum, StreamSliceData } from "@nivo/stream";
import { useClientModelVariables, useModelBaselineEffect } from "@services/api";
import { ClientModelVariable } from "@services/api/types";
import { toLocaleString } from "@utils/numbers";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const BASELINE_SERIES_ID = "baseline";
const BASELINE_COLOR = "#667085";

interface BaselineEffectSeries {
  id: string;
  label: string;
  color: string;
}

interface BaselineEffectDatum {
  period: string;
  [key: string]: number | string;
}

const getModelVarOrder = (model_var: ClientModelVariable): number => {
  return model_var.order;
};

const ModelBaselineEffects = () => {
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mdValue = theme.breakpoints.values.md;

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(1000);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<string[]>([]);

  const { data: modelVars, isFetching: isLoadingModelVars } =
    useClientModelVariables(currentClient.model.id);
  const { data, isFetching: isLoadingModelBaseline } = useModelBaselineEffect(
    currentClient.model.id
  );

  const series = useMemo<BaselineEffectSeries[]>(() => {
    const mediaSeries = (modelVars || [])
      .filter((modelVar) => modelVar.is_significant)
      .sort((a, b) => getModelVarOrder(a) - getModelVarOrder(b))
      .map((modelVar) => ({
        id: modelVar.name,
        label: modelVar.name,
        color: modelVar.color
      }));

    return [
      {
        id: BASELINE_SERIES_ID,
        label: t("mmmodel.inflow_breakdown.chart.legend.baseline"),
        color: BASELINE_COLOR
      },
      ...mediaSeries
    ];
  }, [modelVars, t]);

  const legendSeries = useMemo(() => {
    const baselineSeries = series.find(
      (seriesEntry) => seriesEntry.id === BASELINE_SERIES_ID
    );
    const mediaSeries = series.filter(
      (seriesEntry) => seriesEntry.id !== BASELINE_SERIES_ID
    );

    return baselineSeries ? [...mediaSeries, baselineSeries] : mediaSeries;
  }, [series]);

  const streamData = useMemo<BaselineEffectDatum[]>(() => {
    return (data?.data || []).map((weekData) => {
      const row: BaselineEffectDatum = {
        period: weekData.week_id,
        [BASELINE_SERIES_ID]: weekData.baseline
      };

      series
        .filter((seriesEntry) => seriesEntry.id !== BASELINE_SERIES_ID)
        .forEach((seriesEntry) => {
          row[seriesEntry.id] =
            weekData.media.find((media) => media.name === seriesEntry.id)
              ?.value || 0;
        });

      return row;
    });
  }, [data, series]);

  const activeKeys = useMemo(
    () =>
      series
        .map((seriesEntry) => seriesEntry.id)
        .filter((seriesId) => !hiddenSeriesIds.includes(seriesId)),
    [series, hiddenSeriesIds]
  );

  const seriesColors = useMemo(
    () =>
      series.reduce<Record<string, string>>((acc, seriesEntry) => {
        acc[seriesEntry.id] = seriesEntry.color;
        return acc;
      }, {}),
    [series]
  );

  const axisTickValues = useMemo(() => {
    return streamData.map((_, index) => index);
  }, [streamData]);

  const chartHeight = isMobile ? 420 : 500;
  const chartMargin = isMobile
    ? { top: 20, right: 20, bottom: 80, left: 70 }
    : { top: 20, right: 30, bottom: 160, left: 90 };

  const isLoading = useMemo(
    () => isLoadingModelVars || isLoadingModelBaseline,
    [isLoadingModelVars, isLoadingModelBaseline]
  );

  useEffect(() => {
    setHiddenSeriesIds((currentHiddenSeries) =>
      currentHiddenSeries.filter((seriesId) =>
        series.some((seriesEntry) => seriesEntry.id === seriesId)
      )
    );
  }, [series]);

  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleLegendToggle = (seriesId: string) => {
    setHiddenSeriesIds((currentHiddenSeries) => {
      if (currentHiddenSeries.includes(seriesId)) {
        return currentHiddenSeries.filter((hiddenId) => hiddenId !== seriesId);
      }

      if (series.length - currentHiddenSeries.length <= 1) {
        return currentHiddenSeries;
      }

      return [...currentHiddenSeries, seriesId];
    });
  };

  const renderTooltip = ({ slice }: { slice: StreamSliceData }) => {
    const period = streamData[slice.index]?.period || "";
    const stackedValuesById = slice.stack.reduce<
      Record<string, StreamLayerDatum>
    >((acc, layer) => {
      acc[String(layer.layerId)] = layer;
      return acc;
    }, {});

    const tooltipLayers = legendSeries
      .filter((seriesEntry) => activeKeys.includes(seriesEntry.id))
      .map((seriesEntry) => stackedValuesById[seriesEntry.id])
      .filter((layer): layer is StreamLayerDatum => Boolean(layer));

    const total = tooltipLayers.reduce((sum, layer) => sum + layer.value, 0);

    return (
      <Box
        sx={{
          minWidth: 180,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
          px: 1.75,
          py: 1.5
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, display: "block", mb: 1 }}
        >
          {period}
        </Typography>
        {tooltipLayers.map((layer) => {
          const percentage = total > 0 ? (layer.value / total) * 100 : 0;

          return (
            <Box
              key={String(layer.layerId)}
              sx={{
                display: "grid",
                gridTemplateColumns: "10px 1fr auto auto",
                alignItems: "center",
                gap: 1,
                py: 0.25
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "0%",
                  backgroundColor: layer.color
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {String(layer.layerId) === BASELINE_SERIES_ID
                  ? t("mmmodel.inflow_breakdown.chart.legend.baseline")
                  : String(layer.layerId)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {toLocaleString(percentage, 0)}%
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {toLocaleString(layer.value, 1)}
              </Typography>
            </Box>
          );
        })}
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: `1px solid ${theme.palette.divider}`,
            mt: 1,
            pt: 1
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Total
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {toLocaleString(total, 1)}
          </Typography>
        </Box> */}
      </Box>
    );
  };

  return (
    <Box className="w-full pt-1 pl-4 pr-4" sx={{ height: "100%" }}>
      <Paper elevation={1} sx={{ height: "100%" }} ref={chartContainerRef}>
        {isLoading ? (
          <TitledChartSkeleton hasLegend />
        ) : (
          <>
            <Typography variant="h6" component="div" className="pt-4 pl-6">
              {t("mmmodel.baseline_effect.title")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                px: 4,
                pt: 2,
                pb: 1
              }}
            >
              {legendSeries.map((seriesEntry) => {
                const isActive = activeKeys.includes(seriesEntry.id);

                return (
                  <Box
                    key={seriesEntry.id}
                    component="button"
                    type="button"
                    onClick={() => handleLegendToggle(seriesEntry.id)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      p: 0,
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      opacity: isActive ? 1 : 0.35,
                      color: "text.secondary"
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 0,
                        backgroundColor: seriesEntry.color
                      }}
                    />
                    <Typography variant="caption">
                      {seriesEntry.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                overflowX: isMobile ? "scroll" : "hidden",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none"
                }
              }}
            >
              <Box
                sx={{
                  width: isMobile ? mdValue : chartWidth,
                  minWidth: isMobile ? mdValue : "100%"
                }}
              >
                <Box sx={{ height: chartHeight, position: "relative" }}>
                  {data?.baseline_pct != null ? (
                    <Box
                      sx={{
                        position: "absolute",
                        top: chartMargin.top + 8,
                        left: chartMargin.left + 8,
                        zIndex: 1,
                        backgroundColor: BASELINE_COLOR,
                        color: "#fff",
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 0.5,
                        fontWeight: 700,
                        fontSize: 12,
                        lineHeight: 1.2
                      }}
                    >
                      {`${t("mmmodel.inflow_breakdown.chart.legend.baseline")}: ${toLocaleString(data.baseline_pct, 0)}%`}
                    </Box>
                  ) : null}
                  <Stream
                    data={streamData}
                    keys={activeKeys}
                    width={isMobile ? mdValue : chartWidth}
                    height={chartHeight}
                    margin={chartMargin}
                    theme={chartTheme}
                    offsetType="none"
                    curve="catmullRom"
                    colors={(layer) => seriesColors[String(layer.id)]}
                    fillOpacity={0.9}
                    borderWidth={1}
                    borderColor={{ from: "color" }}
                    enableGridX={false}
                    enableGridY
                    enableDots={false}
                    enableStackTooltip
                    stackTooltip={renderTooltip}
                    axisLeft={{
                      tickSize: 0,
                      tickPadding: 10,
                      legend: currentClient.client.kpi_label,
                      legendOffset: -65,
                      legendPosition: "middle"
                    }}
                    axisBottom={{
                      tickSize: 0,
                      tickPadding: 10,
                      tickRotation: -45,
                      tickValues: axisTickValues,
                      format: (value) => streamData[Number(value)]?.period || ""
                    }}
                  />
                </Box>
                {isMobile ? (
                  <SwipeIcon
                    sx={{
                      opacity: 0.4,
                      ml: "auto",
                      mr: 2,
                      mt: 1,
                      display: "block",
                      color: theme.palette.primary.main
                    }}
                    fontSize="small"
                  />
                ) : null}
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ModelBaselineEffects;
